import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Grid, Row, Col, Text, Button } from 'rsuite';
import {
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressRegistrationVerbiage,
} from '../../../store/slices/config/configSelectors';
import Notice from '../../user-feedback/Notice';
import UsersLicenseRow from './UserLicenseRow';
import pluralize from 'pluralize';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import { C_ACTIVE_PERMIT_STATUSES } from '../../../common/consts';
import { titleize } from '../../../common/utils';
import { License } from 'src/types/licenses';

interface UsersLicensesProps {
  licenses: License[];
  errorMessage?: string;
  taxCertificateEnabled?: boolean;
}

const UsersLicenses = ({
  licenses,
  errorMessage,
  taxCertificateEnabled = false,
}: UsersLicensesProps) => {
  const placeNavigate = usePlaceNavigate();

  const registrationVerbiage = useSelector(
    selectConfigExpressRegistrationVerbiage
  );
  const typeVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);

  const groupAndSortLicenses = (licenses: License[]) => {
    const statusOrder = ['current', 'submitted'];
    let licensesCopy = [...licenses];

    licensesCopy.sort((a, b) => {
      const statusA = statusOrder.indexOf(a.status);
      const statusB = statusOrder.indexOf(b.status);

      if (statusA !== statusB) return statusA - statusB;
      if (a.status === b.status)
        return b.application_number.localeCompare(a.application_number);
      return b.status.localeCompare(a.status);
    });
    return licensesCopy;
  };

  const activeLicenses = useMemo(() => {
    return (licenses ?? []).filter((license) =>
      C_ACTIVE_PERMIT_STATUSES.includes(license.status)
    );
  }, [licenses]);

  const truncatedActiveLicensesToDisplay = useMemo(() => {
    const sortedLicenses = groupAndSortLicenses(activeLicenses);
    return sortedLicenses?.slice(0, 4);
  }, [activeLicenses]);

  const numAdditionalActivePermitsUndisplayed = useMemo(() => {
    return (
      (activeLicenses.length || 0) -
      (truncatedActiveLicensesToDisplay?.length || 0)
    );
  }, [activeLicenses, truncatedActiveLicensesToDisplay]);

  const numAdditionalInactivePermitsUndisplayed = useMemo(() => {
    return (licenses.length || 0) - (activeLicenses?.length || 0);
  }, [licenses, activeLicenses]);

  const numAdditionalPermitsUndisplayedText = useMemo(() => {
    let text = '';
    if (
      activeLicenses.length > 4 &&
      numAdditionalInactivePermitsUndisplayed === 0
    ) {
      text = `+ ${numAdditionalActivePermitsUndisplayed} more Active ${titleize(pluralize(typeVerbiage, numAdditionalActivePermitsUndisplayed))}`;
    } else if (
      activeLicenses.length <= 4 &&
      numAdditionalInactivePermitsUndisplayed > 0
    ) {
      const prefix = activeLicenses.length === 0 ? 'show' : '+';
      text = `${prefix} ${numAdditionalInactivePermitsUndisplayed} Inactive ${titleize(pluralize(typeVerbiage, numAdditionalInactivePermitsUndisplayed))}`;
    } else if (
      numAdditionalInactivePermitsUndisplayed > 0 &&
      activeLicenses.length > 4
    ) {
      text = `+ ${numAdditionalActivePermitsUndisplayed} more Active ${titleize(pluralize(typeVerbiage, numAdditionalActivePermitsUndisplayed))} + ${numAdditionalInactivePermitsUndisplayed} Inactive ${titleize(pluralize(typeVerbiage, numAdditionalInactivePermitsUndisplayed))}`;
    }
    return text;
  }, [
    activeLicenses,
    numAdditionalInactivePermitsUndisplayed,
    numAdditionalActivePermitsUndisplayed,
    typeVerbiage,
  ]);

  if (errorMessage) {
    return (
      <Notice
        message={errorMessage}
        style={{ width: '100%' }}
      />
    );
  }

  if (activeLicenses?.length > 0) {
    return (
      <Grid
        role="grid"
        fluid
        className="grdGreyStripe"
      >
        {truncatedActiveLicensesToDisplay.map((license, index) => {
          return (
            <UsersLicenseRow
              key={`license_${license.license}-${index}`}
              license={license}
              taxCertificateEnabled={taxCertificateEnabled}
            />
          );
        })}
        {(numAdditionalActivePermitsUndisplayed > 0 ||
          numAdditionalInactivePermitsUndisplayed > 0) && (
          <Row>
            <Col xs={24}>
              <Button
                appearance="link"
                style={{ color: 'unset', textDecoration: 'underline' }}
                onClick={() => {
                  placeNavigate('/all-permits');
                }}
              >
                {numAdditionalPermitsUndisplayedText}
              </Button>
            </Col>
          </Row>
        )}
      </Grid>
    );
  }

  return (
    <Grid
      fluid
      className="pv-24"
    >
      <Row>
        <Col
          xs={24}
          className="text-center"
        >
          <Text as="i">{`You don't have any Active ${pluralize(registrationVerbiage || '')}.`}</Text>
        </Col>
      </Row>
      {(numAdditionalActivePermitsUndisplayed > 0 ||
        numAdditionalInactivePermitsUndisplayed > 0) && (
        <Row className="text-center">
          <Col xs={24}>
            <Button
              appearance="link"
              style={{ color: 'unset', textDecoration: 'underline' }}
              onClick={() => {
                placeNavigate('/all-permits');
              }}
            >
              {numAdditionalPermitsUndisplayedText}
            </Button>
          </Col>
        </Row>
      )}
    </Grid>
  );
};

export default UsersLicenses;
